import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {Entity} from '../../entity/entity';
import {EntityLineHeader} from '../../entity/entity-line-header';
import {Riser} from '../../products/riser';
import {EntityLineService} from '../../services/entity-line-service';
import {EntityService} from '../../services/entity-service';
import {ProductService} from '../../services/product-service';
import {StringUtils} from '../../utils/string-utils';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogActions } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

import { FormsModule } from '@angular/forms';

@Component({
    selector: 'riser-dialog',
    templateUrl: 'riser-dialog.component.html',
    styleUrls: ['./riser-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatProgressSpinner, MatDialogActions]
})
export class RiserDialogComponent {
  private readonly productService = inject(ProductService);
  private readonly entityService = inject(EntityService);
  private readonly entityLineService = inject(EntityLineService);
  private dialogRef = inject<MatDialogRef<RiserDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);


  entity: Entity;
  private readonly risers = signal<Riser[]>(undefined!);
  copperFilter = '';
  risersOnly = false;
  diameterFilter = '';
  insulationFilter = '';
  readonly selectedRisers = signal<Riser[]>([]);
  readonly isBusy = signal<boolean>(false);
  readonly errorMessage = signal<string>(undefined!);
  readonly filtered = signal<Riser[]>(undefined!);

  constructor
  () {
    const data = this.data;

    this.isBusy.set(true);
    this.entity = data.entity as Entity;
    this.entityLineService.getRiserLines(this.entity.id).subscribe(results => {
      this.selectedRisers.set(results);
    }, error => this.handleError(error));
    this.productService.getRisers().subscribe(results => {
      this.risers.set(results);
      this.filterList();
      this.isBusy.set(false);
    }, this.handleError);
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  public saveAndClose(): void {
    this.isBusy.set(true);
    this.entityService.addRisers(this.entity.id, this.selectedRisers()).subscribe(result => {
      this.isBusy.set(false);
      this.dialogRef.close(result);
    }, error => this.handleError(error));
  }

  public cancel(): void {
    this.dialogRef.close(false);
  }

  filterList(): void {
    let filtered = this.risers().filter(riser => !this.selectedRisers().map(selected => selected.productGuid).includes(riser.productGuid));
    if (this.copperFilter.length > 0) {
      filtered = filtered.filter(riser => riser.copperType.toUpperCase().includes(this.copperFilter.toUpperCase()));
    }
    if (this.risersOnly) {
      filtered = filtered.filter(riser => riser.modelCode == 'RIS');
    }
    if (this.diameterFilter.length > 0) {
      filtered = filtered.filter(riser => riser.diameter.toUpperCase().includes(this.diameterFilter.toUpperCase()));
    }
    if (this.insulationFilter.length > 0) {
      filtered = filtered.filter(riser => riser.insulation && riser.insulation.toUpperCase().includes(this.insulationFilter.toUpperCase()));
    }
    this.filtered.set(filtered);
  }

  public selectRiser(riser: Riser): void {
    riser.quantity = 1;
    this.selectedRisers.update(current => [...current, riser]);
    this.filterList();
  }

  public removeSelection(riser: Riser): void {
    const index = this.selectedRisers().indexOf(riser, 0);
    if (index > -1) {
      riser.quantity = 0;
      this.selectedRisers.update(current => current.filter((_, riserIndex) => riserIndex !== index));
      if (riser.lineId && riser.lineId.length > 0) {
        this.deleteEntityLine(riser);
      }
      this.filterList();
    }
  }

  private deleteEntityLine(riser: Riser): void {
    this.isBusy.set(true);
    const headers: EntityLineHeader[] = [];
    const header: EntityLineHeader = new EntityLineHeader();
    header.lineId = riser.lineId;
    header.key = this.entity.id;
    headers.push(header);
    this.entityLineService.deleteLines(headers).subscribe(() => this.isBusy.set(false));
  }

}
